import { ChangeDetectorRef, Directive, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { Customer } from '@df/session/api/customer';
import { TaxMode } from '@df/ui/price/price.service';
import { Nexus } from '@tomandco/nexus-api-types';
import omit from 'lodash-es/omit';
import { Subscription } from 'rxjs';

export interface IBasketTotalsItem {
    label: string;
    value: number;
    comment?: string;
    id: string;
}

@Directive()
export abstract class BasketTotalsComponentDef implements OnInit, OnDestroy {
    totals: IBasketTotalsItem[] = [];

    totalsMap: { [totalId: string]: number } = {};

    @Input()
    allowEmptySubtotal = false;

    @Input()
    alwaysDisplaySubtotal = false;

    protected subscriptions = new Subscription();

    protected update!: () => void;

    protected dfEventService = inject(DfEventService);
    protected requestService = inject(RequestService);

    protected removeCouponStatus = new Status();

    /**
     * Custom order
     */
    protected order?: string[];

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        protected app: App,
        protected customer: Customer,
        protected timeoutService: TimeoutService
    ) {}

    ngOnInit(): void {
        this.update = this.timeoutService.debounce(() => this._update(), 100);
        this.setSubscriptions();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get basket() {
        return this.customer.basket;
    }

    protected setSubscriptions(): void {
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (event.id === 'basketDataChange') {
                    this.update();
                }
            })
        );

        this._update();
    }

    protected _update(): void {
        const basketTotals = this.basket.data?.totals;
        const totals: IBasketTotalsItem[] = [];
        const totalsMap: { [totalId: string]: number } = {};

        if (basketTotals) {
            const basketExclude = this.taxConfig.basket.exclude;
            const basketTaxMode = basketExclude ? TaxMode.excluding : TaxMode.including;
            const shippingExclude = this.taxConfig.shipping.exclude;

            const subtotal = this.basket.getSubtotal(basketTaxMode);
            const discount = this.basket.getDiscount(basketTaxMode);
            const giftcards = this.getGiftcardDiscount(basketTaxMode) * -1;

            if (subtotal || this.allowEmptySubtotal) {
                totals.push({ label: this.getSubtotalLabel(basketTaxMode), id: 'subtotal', value: subtotal });
                totalsMap['subtotal'] = subtotal;
            }

            if (discount) {
                totals.push({ label: this.getDiscountLabel(basketTaxMode), id: 'discount', value: discount });
                totalsMap['discount'] = discount;
            }

            if (giftcards) {
                totals.push({ label: this.getGiftcardsLabel(basketTaxMode), id: 'giftcards', value: giftcards });
                totalsMap['giftcards'] = giftcards;
            }

            if (this.withShipping) {
                const shippingTaxMode = shippingExclude ? TaxMode.excluding : TaxMode.including;
                const shippingValue = this.basket.getShipping(shippingTaxMode);
                totals.push({
                    label: this.getShippingLabel(shippingTaxMode),
                    id: 'shipping',
                    value: shippingValue
                });
                totalsMap['shipping'] = shippingValue;
            }

            if (basketTotals.tax && (basketExclude || shippingExclude)) {
                const value =
                    basketExclude && shippingExclude
                        ? basketTotals.tax
                        : basketExclude
                          ? basketTotals.tax - (basketTotals.shipping_tax || 0)
                          : basketTotals.tax - (basketTotals.subtotal_tax || 0) - (this.withShipping ? 0 : basketTotals.shipping_tax || 0);
                if (value) {
                    totals.push({ label: this.getTaxLabel(), id: 'tax', value });
                    totalsMap['tax'] = value;
                }
            }

            const grandTotal = basketTotals.grand_total || 0;
            totals.push({ label: this.getTotalLabel(), id: 'total', value: grandTotal });
            totalsMap['total'] = grandTotal;

            this.addCustomTotals(totals, totalsMap);
        }

        const keys = Object.keys(totalsMap);
        if (!this.alwaysDisplaySubtotal && keys.length === 2 && keys.includes('subtotal') && keys.includes('total')) {
            this.totalsMap = omit(totalsMap, 'subtotal');
            this.setTotals(totals.filter(i => i.id !== 'subtotal'));
        } else {
            this.totalsMap = totalsMap;
            this.setTotals(totals);
        }
    }

    protected addCustomTotals(totals: IBasketTotalsItem[], totalsMap: { [totalId: string]: number }): void {}

    protected setTotals(totals: IBasketTotalsItem[]): void {
        const order = this.order;
        if (order) {
            const sortedTotals = order.map(i => totals.find(t => t.id === i)).filter(Boolean) as IBasketTotalsItem[];
            const otherTotals = totals.filter(t => !order.includes(t.id));
            const totalIndex = sortedTotals.findIndex(i => i.id === 'total');
            sortedTotals.splice(totalIndex === -1 ? sortedTotals.length : totalIndex, 0, ...otherTotals);
            this.totals = sortedTotals;
        } else {
            this.totals = totals;
        }
        this.detectChanges();
    }

    protected getSubtotalLabel(taxMode?: TaxMode): string {
        return 'Subtotal';
    }

    /**
     * @deprecated Typoe, use getSubtatalLabel instead
     * remove this function after GR is fixed
     */
    protected getSubtotlaLabel(taxMode?: TaxMode): string {
        return this.getSubtotalLabel(taxMode);
    }

    protected getDiscountLabel(taxMode?: TaxMode): string {
        return 'Discount';
    }

    protected getGiftcardsLabel(taxMode?: TaxMode): string {
        return 'Gift Cards';
    }

    protected getShippingLabel(taxMode?: TaxMode): string {
        return 'Delivery';
    }

    protected getTaxLabel(): string {
        return this.taxConfig.basket.exclude ? 'Tax' : 'Tax (Included)';
    }

    protected getTotalLabel(): string {
        return this.withShipping ? 'Order total' : 'Order total (Excl. delivery)';
    }

    private getGiftcardDiscount(taxMode: TaxMode = TaxMode.skip): number {
        const giftcards = this.basket.getTotal('giftcards', 'discount_tax', taxMode);
        if (giftcards) {
            return giftcards;
        }
        const amgiftcard = this.basket.getTotal('amgiftcard', 'discount_tax', taxMode);
        if (amgiftcard) {
            return -1 * amgiftcard;
        }
        return 0;
    }

    private get taxConfig(): Nexus.Lib.General.TaxSettings {
        return this.app.data.tax;
    }

    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    protected get withShipping(): boolean {
        return !!this.basket.data?.shippingMethod || !!this.basket.shipping;
    }

    trackById(index: number, total: IBasketTotalsItem): string {
        return total.id;
    }

    /**
     * Sends remove coupon request
     * Takes coupon code to remove as param - if not defined we will take this.coupon
     * that should work for one-coupon websited
     *
     * @param  {string} code Coupon code to remove
     * @return {promise}
     */
    protected removeCoupon() {
        return this.requestService.send(this.removeCouponStatus, () =>
            this.requestService.nexus.coupon.remove({
                coupon: this.basket.data.coupon as string
            })
        );
    }
}
